import type { Provenance } from '@ardoq/api-types';
import type { SourceType } from '@ardoq/api-types/integrations';
import { IconAvatar } from '@ardoq/decorators';
import { colors } from '@ardoq/design-tokens';
import type { I18nLocale } from '@ardoq/i18n';
import { Icon, IconSize, IntegrationIcon, UnknownIcon } from '@ardoq/icons';
import { ardoqUrl } from '@ardoq/integrations/logos';
import { FlexBox } from '@ardoq/layout';
import { WithPopover } from '@ardoq/popovers';
import { Text } from '@ardoq/typography';
import type { ReactElement } from 'react';
import {
  integrationSources,
  isIntegrationProvenanceSource,
} from '@ardoq/integrations/integration-sources';
import {
  getMissingRecordExplanation,
  getUnknownLabel,
} from './provenanceTexts';
import {
  aiAgentSources,
  apiSources,
  getSourceDisplay,
  type SourceDisplay,
  surveySources,
  uiSources,
  unlistedAiAgentSource,
} from './sources';

type SourceLabelProps = {
  icon: ReactElement;
  name: string;
};

const SourceLabel = ({ icon, name }: SourceLabelProps): ReactElement => (
  <FlexBox gap="small" align="center" minWidth="0">
    {icon}
    <Text truncate>{name}</Text>
  </FlexBox>
);

type IntegrationSourceProps = {
  source: SourceType;
};

const IntegrationSource = ({
  source,
}: IntegrationSourceProps): ReactElement => {
  if (!isIntegrationProvenanceSource(source)) {
    return (
      <SourceLabel
        icon={<IntegrationIcon iconSize={IconSize.MEDIUM} />}
        name="Integration"
      />
    );
  }
  const { name, logoUrl } = integrationSources[source];
  return (
    <SourceLabel
      icon={<Icon src={logoUrl} iconSize={IconSize.MEDIUM} />}
      name={name}
    />
  );
};

type AvatarColors = {
  color: string;
  backgroundColor: string;
};

const BRAND_AVATAR_COLORS: AvatarColors = {
  color: colors.iconBrand,
  backgroundColor: colors.surfaceBrand,
};

const AI_AVATAR_COLORS: AvatarColors = {
  color: colors.orange600,
  backgroundColor: colors.orange100,
};

type ListedSourceProps = {
  sourceDisplay: SourceDisplay | undefined;
  avatarColors: AvatarColors;
  i18nLocale: I18nLocale;
};

const ListedSource = ({
  sourceDisplay,
  avatarColors,
  i18nLocale,
}: ListedSourceProps): ReactElement => {
  if (!sourceDisplay) {
    return <UnknownSource i18nLocale={i18nLocale} explanation={null} />;
  }
  return (
    <SourceLabel
      icon={
        <IconAvatar
          iconName={sourceDisplay.iconName}
          iconSize={IconSize.SMALL}
          color={avatarColors.color}
          backgroundColor={avatarColors.backgroundColor}
          size="s24"
        />
      }
      name={sourceDisplay.name}
    />
  );
};

type ArdoqSourceProps = {
  name: string;
};

const ArdoqSource = ({ name }: ArdoqSourceProps): ReactElement => (
  <SourceLabel
    icon={<Icon src={ardoqUrl} iconSize={IconSize.MEDIUM} />}
    name={name}
  />
);

type UiSourceProps = {
  source: string;
  i18nLocale: I18nLocale;
};

const UiSource = ({ source, i18nLocale }: UiSourceProps): ReactElement => {
  const sourceDisplay = getSourceDisplay(uiSources, source);
  if (!sourceDisplay) {
    return <ArdoqSource name="Ardoq UI" />;
  }
  return (
    <ListedSource
      sourceDisplay={sourceDisplay}
      avatarColors={BRAND_AVATAR_COLORS}
      i18nLocale={i18nLocale}
    />
  );
};

type AiAgentSourceProps = {
  source: string;
  i18nLocale: I18nLocale;
};

const AiAgentSource = ({
  source,
  i18nLocale,
}: AiAgentSourceProps): ReactElement => (
  <ListedSource
    sourceDisplay={
      getSourceDisplay(aiAgentSources, source) ?? unlistedAiAgentSource
    }
    avatarColors={AI_AVATAR_COLORS}
    i18nLocale={i18nLocale}
  />
);

type UnknownSourceProps = {
  i18nLocale: I18nLocale;
  explanation: string | null;
};

const UnknownSource = ({
  i18nLocale,
  explanation,
}: UnknownSourceProps): ReactElement => (
  <WithPopover content={explanation}>
    <FlexBox gap="small" align="center" minWidth="0">
      <UnknownIcon iconSize={IconSize.MEDIUM} color={colors.icon} />
      <Text color="textSubtle" truncate>
        {getUnknownLabel(i18nLocale)}
      </Text>
    </FlexBox>
  </WithPopover>
);

type ProvenanceValueProps = {
  provenance: Provenance | null | undefined;
  i18nLocale: I18nLocale;
  hasMissingRecordExplanation: boolean;
};

export const ProvenanceValue = ({
  provenance,
  i18nLocale,
  hasMissingRecordExplanation,
}: ProvenanceValueProps): ReactElement | null => {
  if (provenance === undefined) {
    return null;
  }
  if (provenance === null && hasMissingRecordExplanation) {
    return (
      <UnknownSource
        i18nLocale={i18nLocale}
        explanation={getMissingRecordExplanation(i18nLocale)}
      />
    );
  }
  if (provenance === null) {
    return <UnknownSource i18nLocale={i18nLocale} explanation={null} />;
  }
  switch (provenance.actorKind) {
    case 'integration':
      return <IntegrationSource source={provenance.source} />;
    case 'ui':
      return <UiSource source={provenance.source} i18nLocale={i18nLocale} />;
    case 'api':
      return (
        <ListedSource
          sourceDisplay={getSourceDisplay(apiSources, provenance.source)}
          avatarColors={BRAND_AVATAR_COLORS}
          i18nLocale={i18nLocale}
        />
      );
    case 'survey':
      return (
        <ListedSource
          sourceDisplay={getSourceDisplay(surveySources, provenance.source)}
          avatarColors={BRAND_AVATAR_COLORS}
          i18nLocale={i18nLocale}
        />
      );
    case 'system':
      return <ArdoqSource name="Ardoq System" />;
    case 'ai-agent':
      return (
        <AiAgentSource source={provenance.source} i18nLocale={i18nLocale} />
      );
    default:
      provenance satisfies never;
      return <UnknownSource i18nLocale={i18nLocale} explanation={null} />;
  }
};
